// LUMINA · pages/vajra/TeachingsGateway.jsx
// Phase 68: Vajra Teachings Redesign — Theme Hall (主题门厅)

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../../constants/config';

const GD = "#D4AF37";
const BG = "#FDFBF7";
const CARD_BG = "#1C140E";
const TXT_LIGHT = "rgba(0,0,0,0.82)";
const TXT_MUTED = "rgba(0,0,0,0.50)";

const SEED_SYLLABLES = {
  "dzogchen":         { seed: "आः",   note: "大圆满" },
  "yuthok-nyingtik":  { seed: "ह्रीः", note: "玉妥宁提" },
  "vajrakilaya":      { seed: "हूं",   note: "普巴金刚" },
  "naropa-6-yogas":   { seed: "हीः",   note: "那若六法" },
  "cittamani-tara":   { seed: "तारे",  note: "度母" },
  "sutra":            { seed: "धम्म",  note: "显宗" },
};

const T_GATEWAY = {
  zh: {
    title: "法宝门厅",
    subtitle: "Dharma Teachings Treasury",
    intro: "诸佛法教，阶梯渐进。请选择修行专题进入法宝大厅。",
    back: "返回金刚社区",
    loading: "梵音清雅，正在恭迎法宝...",
    error: "加载失败",
    representative: "代表作",
    lockedNote: "需灌顶",
    unlocked: "已解锁",
    teachingsCount: (total, locked) => `${total}部教法${locked > 0 ? `（${locked}部需灌顶）` : ""}`,
    teachingsUnlocked: (total) => `${total}部教法 ✅`,
    enter: "进入法门 >>",
    noData: "暂无修行专题",
  },
  en: {
    title: "Dharma Treasury Gateway",
    subtitle: "Pathways to the Vajra Teachings",
    intro: "Step by step on the path of Dharma. Please choose a topic to enter the treasury.",
    back: "Back to Community",
    loading: "Welcoming the sacred teachings...",
    error: "Failed to load",
    representative: "Key Texts",
    lockedNote: "Empowerment needed",
    unlocked: "Unlocked",
    teachingsCount: (total, locked) => `${total} Teaching${total > 1 ? "s" : ""}${locked > 0 ? ` (${locked} gated)` : ""}`,
    teachingsUnlocked: (total) => `${total} Teaching${total > 1 ? "s" : ""} ✅`,
    enter: "Enter Topic →",
    noData: "No topics available",
  }
};

const TRADITION_NAMES_EN = {
  "宁玛派": "Nyingma",
  "噶举派": "Kagyu",
  "格鲁派": "Gelug",
  "觉囊派": "Jonang",
  "萨迦派": "Sakya",
  "通用传承": "Universal",
};

const getTraditionColors = (tradition, slug) => {
  const trad = tradition || "";
  if (slug === "yuthok-nyingtik") {
    return {
      primary: "#8B6914",
      gradient: "linear-gradient(135deg, #1C1309, #3A260D)",
      accent: "#D4AF37",
      accentRgb: "212,175,55"
    };
  }
  if (slug === "vajrakilaya") {
    return {
      primary: "#8B1A1A",
      gradient: "linear-gradient(135deg, #FFF5F5, #F0F4FF)",
      accent: "#0D9494",
      accentRgb: "13,148,148"
    };
  }
  if (trad.includes("宁玛")) {
    return {
      primary: "#8B1A1A",
      gradient: "linear-gradient(135deg, #FFF0F0, #F8E8E8)",
      accent: "#F56565",
      accentRgb: "245,101,101"
    };
  }
  if (trad.includes("噶举")) {
    return {
      primary: "#1A5A8B",
      gradient: "linear-gradient(135deg, #F0F4FF, #E0ECFF)",
      accent: "#4299E1",
      accentRgb: "66,153,225"
    };
  }
  if (trad.includes("格鲁")) {
    return {
      primary: "#D4AF37",
      gradient: "linear-gradient(135deg, #FFFDF5, #FDF0D5)",
      accent: "#ECC94B",
      accentRgb: "236,201,75"
    };
  }
  if (trad.includes("觉囊")) {
    return {
      primary: "#8B4513",
      gradient: "linear-gradient(135deg, #1C0D06, #381C0D)",
      accent: "#ED8936",
      accentRgb: "237,137,54"
    };
  }
  if (trad.includes("萨迦")) {
    return {
      primary: "#5B7B8A",
      gradient: "linear-gradient(135deg, #0A111A, #182635)",
      accent: "#63B3ED",
      accentRgb: "99,179,237"
    };
  }
  return {
    primary: "#6B4C3B",
    gradient: "linear-gradient(135deg, #140E0C, #2C1E1A)",
    accent: "#ED8936",
    accentRgb: "237,137,54"
  };
};

export default function TeachingsGateway({ lang = 'zh' }) {
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const [sanskritSupported, setSanskritSupported] = useState(true);
  const token = localStorage.getItem("vajra_token") || "";

  const T = T_GATEWAY[lang] || T_GATEWAY.zh;

  useEffect(() => {
    // Scroll to top
    window.scrollTo(0, 0);

    // Dynamic Sanskrit Font Support Detection using offscreen canvas pixel comparison
    try {
      const canvas = document.createElement("canvas");
      canvas.width = 30;
      canvas.height = 30;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.font = "20px sans-serif";
        ctx.textBaseline = "top";
        ctx.fillText("\uffff", 0, 0);
        const tofu = ctx.getImageData(0, 0, 30, 30).data.toString();
        
        ctx.clearRect(0, 0, 30, 30);
        ctx.fillText("आः", 0, 0);
        const sanskrit = ctx.getImageData(0, 0, 30, 30).data.toString();
        
        setSanskritSupported(tofu !== sanskrit);
      }
    } catch (e) {
      setSanskritSupported(false);
    }

    // Fetch all topic hubs (featured)
    setLoading(true);
    const headers = token ? { "Authorization": `Bearer ${token}` } : {};
    fetch(`${API}/vajra/topic-hubs/featured`, { headers })
      .then(r => {
        if (!r.ok) throw new Error(String(r.status));
        return r.json();
      })
      .then(data => {
        // filter active ones
        const active = data.filter(h => h.is_active);
        // Sort: sort_order asc, then id desc
        const sorted = active.sort((a, b) => {
          if (a.sort_order !== b.sort_order) {
            return (a.sort_order || 0) - (b.sort_order || 0);
          }
          return b.id - a.id;
        });
        setTopics(sorted);
        setLoading(false);
      })
      .catch(err => {
        setError(T.error);
        setLoading(false);
      });
  }, [token, lang]);

  if (loading) {
    return (
      <div style={{
        minHeight: "100vh", background: BG, color: GD, display: "flex",
        flexDirection: "column", alignItems: "center", justifyContent: "center",
        fontFamily: "'Noto Serif SC', serif"
      }}>
        <div style={{ fontSize: 24, marginBottom: 16, animation: "pulse 2s infinite" }}>☸</div>
        <div style={{ fontSize: 14, letterSpacing: 2 }}>{T.loading}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: "100vh", background: BG, color: "#FC8181", display: "flex",
        alignItems: "center", justifyContent: "center", fontFamily: "'Noto Serif SC', serif"
      }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 28, marginBottom: 12 }}>⚠️</div>
          <div style={{ fontSize: 16 }}>{error}</div>
          <button onClick={() => navigate("/vajra")} style={{
            marginTop: 20, background: "none", border: `1px solid ${GD}`, color: GD,
            padding: "8px 16px", borderRadius: 4, cursor: "pointer"
          }}>
            {T.back}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: "100vh", background: BG, color: TXT_LIGHT,
      fontFamily: "'Noto Serif SC', 'Noto Serif', serif", padding: "40px 24px 80px"
    }}>
      <div style={{ maxWidth: 1000, margin: "0 auto" }}>
        {/* Breadcrumb */}
        <div style={{ marginBottom: 24, fontSize: 12, color: TXT_MUTED, fontFamily: "'Noto Sans SC', sans-serif" }}>
          <a href="/vajra" style={{ color: GD, textDecoration: "none" }}>{lang === "zh" ? "金刚社群" : "Vajra Community"}</a>
          {' › '}
          <span style={{ color: TXT_LIGHT }}>{lang === "zh" ? "法宝门厅" : "Teachings Gateway"}</span>
        </div>

        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 60 }}>
          <h1 style={{
            fontSize: 32, letterSpacing: 6, color: GD, fontWeight: 600,
            margin: "0 0 8px 0"
          }}>{T.title}</h1>
          <div style={{
            fontSize: 12, letterSpacing: 3, textTransform: "uppercase",
            color: TXT_MUTED, marginBottom: 20
          }}>{T.subtitle}</div>
          <div style={{
            width: 80, height: 1, background: `linear-gradient(90deg, transparent, ${GD}, transparent)`,
            margin: "0 auto 20px"
          }}></div>
          <p style={{
            fontSize: 14, color: TXT_MUTED, letterSpacing: 1, maxWidth: 500, margin: "0 auto",
            lineHeight: "1.6"
          }}>{T.intro}</p>
        </div>

        {topics.length === 0 ? (
          <div style={{ textAlign: "center", color: TXT_MUTED, fontSize: 14, padding: "40px 0" }}>
            {T.noData}
          </div>
        ) : (
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
            gap: 30,
          }}>
            {topics.map(topic => {
              const colors = getTraditionColors(topic.tradition, topic.slug);
              const seedData = SEED_SYLLABLES[topic.slug] || { seed: "☸", note: "" };
              const hasLocked = topic.locked_count > 0;
              const unlocked = token && hasLocked && topic.user_locked_count === 0;
              const isHovered = hoveredId === topic.id;

              return (
                <div
                  key={topic.id}
                  onMouseEnter={() => setHoveredId(topic.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  onClick={() => navigate(`/vajra/teachings/${topic.slug}`)}
                  style={{
                    background: colors.gradient,
                    borderRadius: 12,
                    border: `1px solid ${isHovered ? colors.accent : "rgba(212,175,55,0.15)"}`,
                    padding: "36px 28px",
                    cursor: "pointer",
                    position: "relative",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    minHeight: 280,
                    transition: "transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease",
                    transform: isHovered ? "translateY(-6px)" : "translateY(0)",
                    boxShadow: isHovered
                      ? `0 12px 36px rgba(${colors.accentRgb}, 0.25)`
                      : "0 4px 15px rgba(0, 0, 0, 0.4)",
                  }}
                >
                  {/* Seed Syllable Watermark */}
                  <div style={{
                    position: "absolute",
                    right: 20,
                    top: 15,
                    fontSize: 100,
                    color: isHovered ? `rgba(${colors.accentRgb}, 0.08)` : "rgba(0,0,0,0.03)",
                    fontFamily: "'Sanskrit', serif",
                    userSelect: "none",
                    pointerEvents: "none",
                    transition: "color 0.3s ease",
                  }}>
                    {sanskritSupported ? seedData.seed : "☸"}
                  </div>

                  {/* Top content */}
                  <div>
                    {/* Category Label */}
                    <div style={{
                      fontSize: 11,
                      color: colors.accent,
                      letterSpacing: 2,
                      textTransform: "uppercase",
                      marginBottom: 12,
                      fontWeight: 600,
                    }}>
                      {lang === "en" && topic.tradition ? (TRADITION_NAMES_EN[topic.tradition] || topic.tradition) : (topic.tradition || "通用传承")}
                    </div>

                    {/* Title */}
                    <h2 style={{
                      fontSize: 22,
                      fontWeight: 400,
                      margin: "0 0 6px 0",
                      color: TXT_LIGHT,
                      letterSpacing: 2,
                    }}>
                      {lang === "en" && topic.title_en ? topic.title_en : topic.title_zh}
                    </h2>

                    {/* Subtitle */}
                    {topic.title_en && lang !== "en" && (
                      <div style={{
                        fontSize: 12,
                        color: TXT_MUTED,
                        fontStyle: "italic",
                        marginBottom: 16,
                      }}>
                        {topic.title_en}
                      </div>
                    )}

                    {/* Counts */}
                    <div style={{
                      fontSize: 13,
                      color: unlocked ? GD : TXT_MUTED,
                      letterSpacing: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      marginBottom: 24,
                    }}>
                      {unlocked ? (
                        T.teachingsUnlocked(topic.teaching_count)
                      ) : (
                        T.teachingsCount(topic.teaching_count, topic.user_locked_count)
                      )}
                    </div>
                  </div>

                  {/* Bottom content */}
                  <div>
                    {/* Representative Teachings */}
                    {topic.representative_teachings && topic.representative_teachings.length > 0 && (
                      <div style={{
                        borderTop: "1px solid rgba(0,0,0,0.06)",
                        paddingTop: 16,
                        marginBottom: 18,
                      }}>
                        <div style={{
                          fontSize: 11,
                          color: TXT_MUTED,
                          letterSpacing: 1,
                          marginBottom: 4,
                          textTransform: "uppercase",
                        }}>
                          {T.representative}:
                        </div>
                        <div style={{
                          fontSize: 12,
                          color: "rgba(0,0,0,0.7)",
                          letterSpacing: 1,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}>
                          {topic.representative_teachings.map(t => (
                            lang === "en" && t.title_en ? t.title_en : t.title_zh
                          )).join(" · ")}
                        </div>
                      </div>
                    )}

                    {/* Action button */}
                    <div style={{
                      display: "flex",
                      justifyContent: "flex-end",
                    }}>
                      <span style={{
                        fontSize: 12,
                        color: isHovered ? colors.accent : GD,
                        letterSpacing: 1,
                        fontWeight: 500,
                        transition: "color 0.2s ease",
                      }}>
                        {T.enter}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
